import { useState } from 'react';
import { Flex, Box, Text, IconButton, Stack } from './';
import {
  Menu as MenuIcon,
  Settings,
  RotateCcw,
  Trophy,
  BarChart2,
  Users,
} from 'lucide-react';
import { SettingsModal, WarningModal, RecordRankedGameModal } from '../modals';
import { useNavigate } from 'react-router-dom';
import { useContext } from 'react';
import { GlobalContext } from '../../helpers/context/GlobalContext';
import {
  hasPlayerNamesEntered,
  hasRoundProgress,
} from '../../helpers/math/spadesMath';
import { getNames } from '../../helpers/utils/storage';
import { initialNames } from '../../helpers/utils/constants';

const Header = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [isRankedModalOpen, setIsRankedModalOpen] = useState(false);
  const { roundHistory, currentRound, setRoundHistory, resetCurrentRound } =
    useContext(GlobalContext);

  const names = getNames() ?? initialNames;
  const hasAnyData =
    hasPlayerNamesEntered(names) ||
    hasRoundProgress(roundHistory, currentRound);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleSettingsClick = () => {
    setIsSettingsOpen(true);
    setIsMenuOpen(false);
  };

  const handleNewGameClick = () => {
    if (hasAnyData) {
      setIsWarningModalOpen(true);
    } else {
      // If no data to clear, just ensure everything is reset and stay on/go to home
      setRoundHistory([]);
      resetCurrentRound();
      navigate('/');
    }
    setIsMenuOpen(false);
  };

  const handleNavigateHome = () => {
    navigate('/');
  };

  return (
    <Box as="header" width="100%" py={0} position="relative" zIndex={100}>
      <Flex
        justify="space-between"
        align="center"
        position="relative"
        zIndex={110}
      >
        <Text
          fontSize="var(--app-font-xl)"
          fontWeight="bold"
          letterSpacing="tight"
          cursor="pointer"
          onClick={handleNavigateHome}
        >
          SpadesCalculator
        </Text>
        <IconButton variant="ghost" onClick={toggleMenu} aria-label="Open Menu">
          <MenuIcon size={24} />
        </IconButton>
      </Flex>

      {isMenuOpen && (
        <>
          <Box
            position="fixed"
            top="0"
            left="0"
            w="100vw"
            h="100vh"
            zIndex="100"
            onClick={() => setIsMenuOpen(false)}
          />
          <Box
            position="absolute"
            top="100%"
            right="0"
            mt={0}
            bg="bg"
            border="1px solid"
            borderColor="whiteAlpha.200"
            borderRadius="md"
            boxShadow="xl"
            overflow="hidden"
            minW="200px"
            zIndex="110"
          >
            <Stack gap={0}>
              <Flex
                px={4}
                py={3}
                align="center"
                cursor="pointer"
                _hover={{ bg: 'whiteAlpha.100' }}
                onClick={handleNewGameClick}
                gap={3}
              >
                <RotateCcw size={18} />
                <Text fontSize="md">New Game</Text>
              </Flex>
              <Flex
                px={4}
                py={3}
                align="center"
                cursor="pointer"
                _hover={{ bg: 'whiteAlpha.100' }}
                onClick={() => {
                  navigate('/players');
                  setIsMenuOpen(false);
                }}
                gap={3}
              >
                <Users size={18} />
                <Text fontSize="md">Players</Text>
              </Flex>
              <Flex
                px={4}
                py={3}
                align="center"
                cursor={roundHistory.length > 0 ? 'pointer' : 'not-allowed'}
                opacity={roundHistory.length > 0 ? 1 : 0.4}
                _hover={{ bg: roundHistory.length > 0 ? 'whiteAlpha.100' : undefined }}
                onClick={
                  roundHistory.length > 0
                    ? () => {
                        setIsRankedModalOpen(true);
                        setIsMenuOpen(false);
                      }
                    : undefined
                }
                gap={3}
              >
                <Trophy size={18} />
                <Text fontSize="md">Record as Ranked</Text>
              </Flex>
              <Flex
                px={4}
                py={3}
                align="center"
                cursor="pointer"
                _hover={{ bg: 'whiteAlpha.100' }}
                onClick={() => {
                  navigate('/leaderboard');
                  setIsMenuOpen(false);
                }}
                gap={3}
              >
                <BarChart2 size={18} />
                <Text fontSize="md">Leaderboard</Text>
              </Flex>
              <Flex
                px={4}
                py={3}
                align="center"
                cursor="pointer"
                _hover={{ bg: 'whiteAlpha.100' }}
                onClick={handleSettingsClick}
                gap={3}
              >
                <Settings size={18} />
                <Text fontSize="md">Settings</Text>
              </Flex>
            </Stack>
          </Box>
        </>
      )}

      <SettingsModal isOpen={isSettingsOpen} setIsOpen={setIsSettingsOpen} />
      <WarningModal
        isOpen={isWarningModalOpen}
        setIsModalOpen={setIsWarningModalOpen}
      />
      <RecordRankedGameModal
        isOpen={isRankedModalOpen}
        onClose={() => setIsRankedModalOpen(false)}
        onSuccess={() => {
          setIsRankedModalOpen(false);
          setRoundHistory([]);
          resetCurrentRound();
          navigate('/leaderboard');
        }}
        roundHistory={roundHistory}
      />
    </Box>
  );
};

export default Header;
